import { suporte } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { Logo } from './Logo';
import { NavItem, NavItemDisabled } from './NavItem';

/**
 * Navegação da organização (spec §24).
 *
 * Os itens seguem as permissões do usuário: mostrar uma tela que responderia
 * 403 em toda consulta é pior do que não mostrar. Itens desabilitados são
 * fases ainda não construídas — ficam visíveis de propósito, para dar noção do
 * produto completo.
 */
export function Sidebar() {
  const { usuario, pode, liberado, ehSuperadmin, sair } = useAuth();
  const emSuporte = suporte.atual() !== null;

  const papel = ehSuperadmin
    ? 'Superadmin'
    : (usuario?.organization?.tradeName ?? 'Organização');

  return (
    <aside className="sidebar">
      <Logo />

      <nav className="sidebar-nav">
        <div className="nav-section-label">Principal</div>
        <NavItem to="/dashboard" icon="ti-layout-dashboard">
          Dashboard
        </NavItem>

        {/* A administração da plataforma mora no painel Master. Durante um
            acesso de suporte o link some: ele levava de volta ao Master ainda
            "dentro" do cliente, e o painel passava a mostrar o recorte daquele
            cliente como se fosse a plataforma. A saída é "Encerrar acesso", na
            faixa roxa do topo. */}
        {ehSuperadmin && !emSuporte && (
          <NavItem to="/master" icon="ti-shield-lock">
            Painel Master
          </NavItem>
        )}

        {pode('events.read') && (
          <NavItem to="/eventos" icon="ti-calendar-star">
            Eventos
          </NavItem>
        )}


        {/* Jornada é sempre DE UM EVENTO: o item mora dentro do evento, em
            `/eventos/:id/jornadas`, e não há uma lista global que faça sentido —
            "todas as jornadas do cliente" misturaria públicos que nunca se
            encontram. Por isso a seção começa pelo que é global de verdade. */}
        <div className="nav-section-label">Jornadas</div>
        {liberado('jornadas') && pode('events.read') ? (
          <NavItem to="/agendamentos" icon="ti-clock-play">
            Agendamentos
          </NavItem>
        ) : (
          <NavItemDisabled icon="ti-clock-play">Agendamentos</NavItemDisabled>
        )}

        {/* "WhatsApp" e não "Central de conversas": é assim que quem atende
            chama a tela, e o nome antigo descrevia o formato em vez do canal.
            O ícone é o do WhatsApp desde 16/10/2026, a pedido do Gabriel. Era o
            de conversa, para separar desta a aba WhatsApp de Configurações — mas
            aquela é uma aba dentro de outra tela, não um item do menu, e quem
            procura o atendimento procura o símbolo do canal.

            A tela existe e está testada, mas a instalação pode não tê-la
            liberado (`JOURNEYS_ENABLED`). Enquanto não liberar, recebe o mesmo
            tratamento das fases futuras: cinza, com "Em breve" no tooltip. */}
        {liberado('jornadas') && pode('conversations.read') ? (
          <NavItem to="/conversas" icon="ti-brand-whatsapp">
            WhatsApp
          </NavItem>
        ) : (
          <NavItemDisabled icon="ti-brand-whatsapp">WhatsApp</NavItemDisabled>
        )}
        {pode('templates.manage') && (
          <NavItem to="/templates" icon="ti-template">
            Templates
          </NavItem>
        )}

        <div className="nav-section-label">Inteligência</div>
        {/* "Recomendações" saiu daqui, e não foi esquecimento: a tela existe e
            mora DENTRO do evento, em `/eventos/:id/recomendacoes`, pelo mesmo
            motivo das jornadas — uma indicação é de um evento, e uma lista
            global misturaria públicos que nunca se encontram. Deixá-la aqui como
            "Em breve" diria que não existe, o que passou a ser falso. */}
        {/* Antes de Matchmaking de propósito: é o perfil que alimenta o
            cruzamento, então esta é a tela que se olha primeiro. E ela foi o
            primeiro jeito de LER um perfil no produto — até 12/10/2026 os onze
            blocos, a síntese e o perfil psicológico eram gravados e nenhuma
            rota os devolvia. */}
        {liberado('jornadas') && pode('participants.read') ? (
          <NavItem to="/participantes" icon="ti-user-search">
            Participantes
          </NavItem>
        ) : (
          <NavItemDisabled icon="ti-user-search">Participantes</NavItemDisabled>
        )}
        <NavItemDisabled icon="ti-affiliate">Matchmaking</NavItemDisabled>
        <NavItemDisabled icon="ti-users-group">Conexões</NavItemDisabled>
        <NavItemDisabled icon="ti-target-arrow">Oportunidades</NavItemDisabled>

        <div className="nav-section-label">Configuração</div>
        {/* Um item só, com abas dentro: dados da empresa, WhatsApp, equipe e
            auditoria. Eram três itens soltos, do mesmo tamanho de Eventos —
            e configuração não disputa atenção com o trabalho do dia; precisa
            ser encontrável, não visível o tempo todo.

            Aparece para quem alcança QUALQUER uma das abas. A própria tela
            filtra as abas uma a uma, então ninguém entra e encontra 403. */}
        {(pode('settings.manage') ||
          pode('integrations.manage') ||
          pode('users.manage') ||
          pode('audit.read')) && (
          <NavItem to="/configuracoes" icon="ti-settings">
            Configurações
          </NavItem>
        )}
        <NavItemDisabled icon="ti-chart-bar">Relatórios</NavItemDisabled>
      </nav>

      <div className="sidebar-user">
        <div className="user-avatar">{iniciais(usuario?.name)}</div>
        <div className="user-info">
          <div className="user-name">{usuario?.name ?? '—'}</div>
          <div className="user-role">{papel}</div>
        </div>
        <button className="icon-btn" title="Sair" onClick={() => void sair()}>
          <i className="ti ti-logout" style={{ fontSize: 15 }} />
        </button>
      </div>
    </aside>
  );
}

function iniciais(nome?: string) {
  if (!nome) return '—';

  const partes = nome.trim().split(/\s+/);
  const primeira = partes[0]?.[0] ?? '';
  // Última palavra, não a segunda: "Maria da Silva" vira MS, não MD.
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';

  return (primeira + ultima).toUpperCase();
}
